import { getGlobalSentry, type SentryModule } from './sentryGlobal';

// Get Sentry instance
export function getSentry(): SentryModule | null {
  return getGlobalSentry();
}

// Helper to get file extension without revealing filename (GDPR)
export function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot >= 0 ? filename.substring(lastDot) : '.unknown';
}


// Helper to parse browser info anonymously
export function getBrowserInfo(): { browserName: string; browserVersion: string; memory?: number; onLine: boolean } {
  const ua = navigator.userAgent;
  let browserName = 'unknown';
  let browserVersion = 'unknown';

  // Parse browser name and version without exposing full user agent
  if (ua.includes('Chrome/')) {
    browserName = 'Chrome';
    const match = /Chrome\/(\d+)/.exec(ua);
    browserVersion = match ? match[1] : 'unknown';
  } else if (ua.includes('Firefox/')) {
    browserName = 'Firefox';
    const match = /Firefox\/(\d+)/.exec(ua);
    browserVersion = match ? match[1] : 'unknown';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browserName = 'Safari';
    const match = /Version\/(\d+)/.exec(ua);
    browserVersion = match ? match[1] : 'unknown';
  } else if (ua.includes('Edge/')) {
    browserName = 'Edge';
    const match = /Edge\/(\d+)/.exec(ua);
    browserVersion = match ? match[1] : 'unknown';
  }

  return {
    browserName,
    browserVersion,
    memory: getBrowserMemory(),
    onLine: navigator.onLine
  };
}

// Helper to get browser memory info
export function getBrowserMemory(): number | undefined {
  // @ts-expect-error - performance.memory is a non-standard API available in Chrome
  if (performance.memory) {
    // @ts-expect-error - accessing non-standard memory property
    return Math.round(performance.memory.usedJSHeapSize / 1048576); // Convert to MB
  }
  return undefined;
}

// Helper to get network connection type
export function getNetworkType(): string {
  // @ts-expect-error - navigator.connection is experimental Network Information API
  if (navigator.connection && navigator.connection.effectiveType) {
    // @ts-expect-error - accessing experimental effectiveType property
    return navigator.connection.effectiveType;
  }
  return 'unknown';
}

// Helper to calculate transfer speed (can be used for uploads, downloads, etc.)
export function calculateTransferSpeed(bytesTransferred: number, durationMs: number): number | undefined {
  if (durationMs > 0) {
    return Math.round((bytesTransferred / 1024) / (durationMs / 1000)); // KB/s
  }
  return undefined;
}

// Initialize general Sentry monitoring (network, visibility changes)
export function initializeGeneralSentryMonitoring() {
  const sentry = getSentry();
  if (!sentry) {
    return;
  }

  // Monitor network status changes
  window.addEventListener('online', () => {
    const sentryInstance = getSentry();
    if (sentryInstance) {
      sentryInstance.addBreadcrumb({
        category: 'network',
        message: 'Network connection restored',
        level: 'info'
      });
    }
  });

  window.addEventListener('offline', () => {
    const sentryInstance = getSentry();
    if (sentryInstance) {
      sentryInstance.addBreadcrumb({
        category: 'network',
        message: 'Network connection lost',
        level: 'warning'
      });
    }
  });

  // Monitor visibility changes (tab switching)
  document.addEventListener('visibilitychange', () => {
    const sentryInstance = getSentry();
    if (document.hidden && sentryInstance) {
      sentryInstance.addBreadcrumb({
        category: 'visibility',
        message: 'Tab became hidden',
        level: 'info'
      });
    }
  });
}

// ============================================================================
// Generic Activity Context Management System
// ============================================================================

// Type for tracking where uploads originate from
export type TrackedUploadLocation = 'web_client_upload' | 'shared_upload' | 'shared_folder_upload';

// Upload-specific data stored in ActivityContext.data
export interface UploadActivityData {
  mimirItemId?: string; // Real Mimir GUID (set when item is created)
  fileExtension: string; // Only extension, not full filename (GDPR)
  fileSize: number;
  fileType: string;
  uploadLocationId?: TrackedUploadLocation;
  isMultipart: boolean;
  chunkSize?: number;
  totalChunks?: number;
  networkSpeed?: number;
  browserInfo: {
    browserName: string;
    browserVersion: string;
    memory?: number;
    onLine: boolean;
  };
}

/**
 * Base interface for any trackable activity in the application
 */
export interface ActivityContext {
  /** Unique identifier for this activity instance */
  id: string;
  /** Type of activity (e.g., 'upload', 'download', 'report_generation') */
  type: string;
  /** Human-readable status of the activity */
  status: string;
  /** When the activity started */
  startTime: number;
  /** Additional activity-specific data */
  data: UploadActivityData;
}

/**
 * Activity type definition for registering new activity types
 */
export interface ActivityType {
  /** Unique name for this activity type */
  name: string;
  /** Sentry context key (e.g., 'active_uploads') */
  contextKey: string;
  /** Sentry tags to set for this activity type */
  tags?: {
    /** Tag name for count (e.g., 'active_upload_count') */
    countTag?: string;
    /** Tag name for IDs (e.g., 'active_mimir_ids') */
    idsTag?: string;
    /** Additional custom tags */
    customTags?: Record<string, (activities: ActivityContext[]) => string | null>;
  };
  /** Transform activities into Sentry context format */
  contextTransformer?: (activities: ActivityContext[]) => Record<string, any>;
}

/**
 * Manages all activity contexts and updates Sentry global context
 */
class SentryContextManager {
  private activityTypes = new Map<string, ActivityType>();
  private activities = new Map<string, Map<string, ActivityContext>>();
  /**
   * Register a new activity type
   */
  registerActivityType(activityType: ActivityType) {
    this.activityTypes.set(activityType.name, activityType);
    this.activities.set(activityType.name, new Map());
  }
  /**
   * Add or update an activity
   */
  setActivity(type: string, activity: ActivityContext) {
    const activities = this.activities.get(type);
    if (!activities) {
      console.warn(`Activity type ${type} not registered`);
      return;
    }
    activities.set(activity.id, activity);
    this.updateGlobalContext();
  }
  /**
   * Remove an activity
   */
  removeActivity(type: string, activityId: string) {
    const activities = this.activities.get(type);
    if (!activities) {
      return;
    }
    activities.delete(activityId);
    this.updateGlobalContext();
  }
  /**
   * Get all activities of a specific type
   */
  getActivities(type: string): ActivityContext[] {
    const activities = this.activities.get(type);
    return activities ? Array.from(activities.values()) : [];
  }
  /**
   * Update Sentry global context with all active activities
   */
  private updateGlobalContext() {
    const sentry = getSentry();
    if (!sentry) {
      return;
    }
    // Process each activity type
    this.activityTypes.forEach((activityType, typeName) => {
      const activities = this.getActivities(typeName);
      if (activities.length === 0) {
        // Clear context and tags for this activity type
        sentry.setContext(activityType.contextKey, null);
        if (activityType.tags?.countTag) {
          sentry.setTag(activityType.tags.countTag, null);
        }
        if (activityType.tags?.idsTag) {
          sentry.setTag(activityType.tags.idsTag, null);
        }
        if (activityType.tags?.customTags) {
          Object.keys(activityType.tags.customTags).forEach(tagName => {
            sentry.setTag(tagName, null);
          });
        }
      } else {
        // Set context
        const context = activityType.contextTransformer
          ? activityType.contextTransformer(activities)
          : this.defaultContextTransformer(activities);
        sentry.setContext(activityType.contextKey, context);
        // Set tags
        if (activityType.tags?.countTag) {
          sentry.setTag(activityType.tags.countTag, activities.length.toString());
        }
        if (activityType.tags?.idsTag) {
          const ids = activities.map(a => a.id).join(',');
          sentry.setTag(activityType.tags.idsTag, ids);
        }
        if (activityType.tags?.customTags) {
          Object.entries(activityType.tags.customTags).forEach(([tagName, tagGetter]) => {
            const value = tagGetter(activities);
            sentry.setTag(tagName, value);
          });
        }
      }
    });
    // Set overall activity summary
    const totalActivities = Array.from(this.activities.values())
      .reduce((sum, map) => sum + map.size, 0);
    if (totalActivities > 0) {
      sentry.setContext('active_processes', {
        total_count: totalActivities,
        by_type: Object.fromEntries(
          Array.from(this.activities.entries())
            .filter(([_, map]) => map.size > 0)
            .map(([type, map]) => [type, map.size])
        )
      });
    } else {
      sentry.setContext('active_processes', null);
    }
  }
  /**
   * Default context transformer - converts activities to a simple object format
   */
  private defaultContextTransformer(activities: ActivityContext[]): Record<string, any> {
    const context: Record<string, any> = {};
    activities.forEach(activity => {
      context[activity.id] = {
        status: activity.status,
        duration_seconds: Math.round((Date.now() - activity.startTime) / 1000),
        ...activity.data
      };
    });
    return context;
  }
}

// Global instance
export const sentryContextManager = new SentryContextManager();
